import React, { useRef, useState } from 'react';
import { tw } from 'twind';
import { MinusSmIcon, PlusSmIcon } from '@heroicons/react/solid';
import { useCartStore } from 'store/cart';
import { CartQuery } from 'lib/api';
import useItemPrice from 'hooks/useItemPrice';
import useConfettiExplosion from 'hooks/useConfettiExplosion';
import useOnScreen from 'hooks/useOnScreen';
import useScreenSize from 'hooks/useScreenSize';
import CartItemOptionSelector from 'components/CartItemOptionSelector';
import { Price } from 'components/Price';
import Description from '@/components/NewCart/Description';
import Reactions from '@/components/NewCart/Reactions';
import { Reaction } from '@/components/NewCart/Reaction';
import Transition from '@/components/NewCart/Transition';
import ImageGallery from '@/components/NewCart/ImageGallery/ImageGallery';
import ReactionsToggle from '@/components/NewCart/ReactionsToggle';

const QtyButton: React.FC<{
  className?: string;
  disabled?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}> = ({ children, className, disabled, onClick }) => {
  if (!disabled) {
    return (
      <button className={tw`inline-flex items-center border ${className}`} onClick={onClick}>
        {children}
      </button>
    );
  }

  return (
    <button className={tw`inline-flex items-center border ${className} bg-general-disabled cursor-not-allowed`} disabled={true}>
      {children}
    </button>
  );
};

const QtyModifier: React.FC<{
  theme: string | null;
  onPlusClick: React.MouseEventHandler<HTMLButtonElement>;
  onMinusClick: React.MouseEventHandler<HTMLButtonElement>;
  itemIsMaxed: boolean;
  quantity: number;
}> = ({ theme, itemIsMaxed, onMinusClick, onPlusClick, quantity }) => (
  <div className={tw`flex`}>
    <QtyButton className={tw`border-r-0 border-${theme}-secondary rounded-l`} onClick={onMinusClick}>
      <MinusSmIcon className={tw`h-4 w-4 mx-1`} aria-hidden="true" />
    </QtyButton>

    <span className={tw`px-2 border-${theme}-secondary border w-9 text-center`}>{quantity}</span>

    <QtyButton className={tw`border-l-0 border-${theme}-secondary rounded-r`} onClick={onPlusClick} disabled={itemIsMaxed}>
      <PlusSmIcon className={tw`h-4 w-4 mx-1`} aria-hidden="true" />
    </QtyButton>
  </div>
);

const disappearance = {
  duration: 0,
  defaultStyle: {
    opacity: 0,
  },
  transitionStyles: {
    entering: { opacity: 1 },
    entered: { opacity: 1 },
    exiting: { opacity: 0 },
    exited: { opacity: 0 },
  },
  unmountOnExit: true,
  mountOnEnter: true,
};

const duration = 500;

const repaint = {
  duration,
  defaultStyle: {
    background: 'linear-gradient(to right, white 50%, black 50%)',
    backgroundSize: '300% 100%',
    backgroundPosition: 'left bottom',
    transition: `all ${duration}ms ease`,
  },
  transitionStyles: {
    entering: { background: 'linear-gradient(to right, white 50%, black 50%)', backgroundSize: '300% 100%' },
    entered: { background: 'linear-gradient(to right, white 50%, black 50%)', backgroundSize: '300% 100%' },
    exiting: { backgroundPosition: 'right bottom' },
    exited: { backgroundPosition: 'right bottom' },
  },
};

export const CartItem: React.FC<{
  item: CartQuery['Cart']['items'][number];
  applyDiscounts: boolean;
  usesDiscountModal: boolean;
  unlimitedProductsIds: string[];
}> = ({ item, applyDiscounts, usesDiscountModal, unlimitedProductsIds }) => {
  const { theme, currency, selectedQuantities, selectedVariations, selectedVariants, incrementQuantity, decrementQuantity, selectOption } =
    useCartStore();

  const isUnlimitedProduct = unlimitedProductsIds.includes(item.id);
  const isLimitedProduct = theme === 'OneItem' && !isUnlimitedProduct;

  const ref = useRef<HTMLDivElement | null>(null);
  const onScreen = useOnScreen(ref);
  const { width: screenWidth, height: screenHeight } = useScreenSize();
  const reactionDuration = 500;
  const confetti = useConfettiExplosion(applyDiscounts && usesDiscountModal && onScreen, {
    force: 0.6,
    particleCount: 40,
    duration: reactionDuration,
    width: 200,
    height: screenHeight,
  });

  const variations = selectedVariations[item.id];
  const variant = selectedVariants[item.id];
  const quantity = selectedQuantities[item.id];
  const price = useItemPrice(variant.price, currency, applyDiscounts);

  const onMinusClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    decrementQuantity(item.id, applyDiscounts);
  };
  const onPlusClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();

    if (!isLimitedProduct || quantity < 1) {
      incrementQuantity(item.id, applyDiscounts);
    }
  };

  const [showPickedReactionAnimation, setShowPickedReactionAnimation] = useState(false);

  const [pickedReaction, pickReaction] = useState<Reaction | null>(null);

  const [showReactions, setShowReactions] = useState<boolean>(false);

  const [doRepaint, setDoRepaint] = useState<boolean>(true);

  const [showOptions, setShowOptions] = useState<boolean>(false);

  const handleOpenReactionsMenu = () => {
    setShowPickedReactionAnimation(false);
    setDoRepaint(false);
    setShowReactions(true);
    setShowOptions(true);
  };

  const handleCloseReactionsMenu = () => {
    setShowReactions(false);
    setTimeout(() => setDoRepaint(true), 250);
  };

  const handlePickReaction = (reaction: Reaction) => {
    setShowOptions(false);
    pickReaction(reaction);
    setShowPickedReactionAnimation(true);
    setTimeout(handleCloseReactionsMenu, reactionDuration);
  };

  const reactionsEnabled = true;

  const urls = item.images.map(({ url }) => url) as string[];

  return (
    <div className={tw(`mb-[24px] shadow-${theme}-cart-box w-full max-w-3xl`)}>
      <ImageGallery urls={urls} />
      <Description name={item.name} description={item.description} />

      {(reactionsEnabled || !!item.options.length) && (
        <>
          <Transition show={doRepaint} {...repaint}>
            <div className={tw`p-2 flex items-center justify-between border-t border-l border-r border-${theme}-secondary h-10`}>
              <Transition show={doRepaint} {...disappearance}>
                <>
                  <div className={tw`flex pr-2`}>
                    {item.options.length > 0 &&
                      item.options.map((option) => (
                        <CartItemOptionSelector
                          key={option.value}
                          label={option.label}
                          value={option.choices.find((c) => c.value === variations[option.value])}
                          onChange={(val) => selectOption(item.id, { [option.value]: val })}
                          options={option.choices}
                        />
                      ))}
                  </div>
                  <>{reactionsEnabled && <ReactionsToggle pickedReaction={pickedReaction} handleToggle={handleOpenReactionsMenu} />}</>
                </>
              </Transition>
              <Reactions
                productId={item.id}
                pickedReaction={pickedReaction}
                pickReaction={handlePickReaction}
                showReactions={showReactions}
                showOptions={showOptions}
                showPickedReactionAnimation={showPickedReactionAnimation}
                screenWidth={screenWidth}
              />
            </div>
          </Transition>
        </>
      )}

      <div className={tw`px-[8px] py-[3px] rounded-b flex justify-between border border-${theme}-secondary text-${theme}-primary`}>
        {variant?.soldOut ? (
          <div
            className={tw`flex items-center justify-center px-2 rounded-full uppercase bg-general-soldout text-general-white font-medium text-xs`}
          >
            Sold out
          </div>
        ) : (
          <QtyModifier
            onMinusClick={onMinusClick}
            onPlusClick={onPlusClick}
            theme={theme}
            itemIsMaxed={isLimitedProduct && quantity > 0}
            quantity={quantity}
          />
        )}

        <div ref={ref} className={tw`self-end`}>
          <Price price={price} />
          {confetti}
        </div>
      </div>
    </div>
  );
};
